import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { ApiEnvelope } from '@/types/package';
import { unwrap } from '@/utils/apiEnvelope';

// Dash Rent — the fleet-wide, LIVE escalation thresholds. One row on the
// backend (setting_rent_escalation); changing it moves every active rental's
// rung on the next 06:30 escalation pass, which is why PUT hands back an
// impact preview and every save lands in a history trail.
const BASE = '/rent/mgmt/v1/escalation-setting';

export interface RentEscalationSetting {
  /** Days of arrears before the rung reaches SHUTDOWN_PENDING. */
  lateShutdownDays: number;
  /** Days of arrears before REPO_REQUESTED. Must exceed lateShutdownDays. */
  lateRepossessionDays: number;
  /** When on, the escalation job opens the repossession case itself. */
  autoRepossessionEnabled: boolean;
  // Null until the first save: a fresh environment runs on the defaults.
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface EscalationImpactRow {
  rentalID: string;
  rentalCode: string;
  from: string;
  to: string;
}

/** What the next escalation run would do under a candidate setting. */
export interface EscalationImpactPreview {
  rentalsChecked: number;
  transitions: number;
  toNormal: number;
  toWarned: number;
  toShutdownPending: number;
  toRepoRequested: number;
  /** Rentals at REPO_REQUESTED after the pass. */
  atRepoRequested: number;
  /** 0 whenever the auto toggle is off. */
  wouldAutoCreateRepossession: number;
  /** Only rentals moving to a HARSHER rung; capped server-side at 100. */
  escalating: EscalationImpactRow[];
}

export interface UpdateRentEscalationSettingBody {
  lateShutdownDays: number;
  lateRepossessionDays: number;
  autoRepossessionEnabled: boolean;
  notes?: string;
  /** true = preview only, nothing is written. */
  dryRun?: boolean;
}

export interface UpdateRentEscalationSettingResult {
  setting: RentEscalationSetting;
  impactPreview: EscalationImpactPreview;
  dryRun: boolean;
}

export interface RentEscalationSettingHistoryEntry {
  ID: string;
  lateShutdownDays: number;
  lateRepossessionDays: number;
  autoRepossessionEnabled: boolean;
  notes: string | null;
  createdAt: string;
  createdBy: string;
}

export const getRentEscalationSetting =
  async (): Promise<RentEscalationSetting> => {
    try {
      const response =
        await Axios.get<ApiEnvelope<RentEscalationSetting>>(BASE);
      return unwrap(response.data);
    } catch (error: unknown) {
      throw new Error(getBackendErrorMessage(error));
    }
  };

/**
 * Full replace. The actor is NOT in the body: the backend reads it off the
 * session token (same as every other rent/mgmt write), so there is nothing
 * for a call site to forget here.
 */
export const updateRentEscalationSetting = async (
  body: UpdateRentEscalationSettingBody
): Promise<UpdateRentEscalationSettingResult> => {
  try {
    const response = await Axios.put<
      ApiEnvelope<UpdateRentEscalationSettingResult>
    >(BASE, body);
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** Same call with `dryRun: true` — the preview alone, nothing written. */
export const previewRentEscalationSetting = (
  body: Omit<UpdateRentEscalationSettingBody, 'dryRun'>
): Promise<UpdateRentEscalationSettingResult> =>
  updateRentEscalationSetting({ ...body, dryRun: true });

export const getRentEscalationSettingHistory = async (
  limit = 50
): Promise<RentEscalationSettingHistoryEntry[]> => {
  try {
    const response = await Axios.get<
      ApiEnvelope<RentEscalationSettingHistoryEntry[]>
    >(`${BASE}/history`, { params: { limit } });
    return unwrap(response.data) ?? [];
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
